// Turns the text of a PDF (as pdf.js extracts it: positioned pieces of text with their fonts)
// into source book sections. No imports, so it can be run on its own against a real PDF.
//
// The steps:
// 1. Each page's text is put back into reading order: by column, with rows that span both
//    columns (wide tables) kept together. Page furniture (headers, footers, page numbers) is dropped.
// 2. Each text style (font + size) is looked at. Bold styles noticeably bigger than the body text
//    are headings, ranked by size into levels. The levels can be changed before building.
// 3. Headings start sections. Everything between them becomes the section's content: paragraphs
//    (with bold and italic kept) and tables.

export interface PdfTextItem {
  str: string;
  font: string;   // The font's real name, without the subset prefix, like "Cambria-Bold"
  size: number;
  x: number;      // Left edge
  y: number;      // Baseline, measured up from the bottom of the page
  width: number;
}

export interface PdfPageText {
  number: number;
  width: number;
  height: number;
  items: PdfTextItem[];
}

export interface TextStyle {
  key: string;
  font: string;
  size: number;
  chars: number;
  lines: number;
  samples: string[];
  level: number;           // Heading level, starting at 1. 0 if it isn't a heading.
  suggested_level: number; // What it was worked out to be.
}

export interface ImportedSection {
  name: string;
  content: string;
  page: number;
  children: ImportedSection[];
}

export interface PdfAnalysis {
  lines: Line[];
  styles: TextStyle[];
  body_key: string;
}

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

const isBoldFont = (font: string) => /bold|semibold|demi|black|heavy/i.test(font);
const isItalicFont = (font: string) => /italic|oblique/i.test(font);
// Small caps fonts draw lowercase letters as smaller capitals, and pdf.js gives them back as capitals.
const isSmallCapsFont = (font: string) => /(^|[-_])SC\d*($|[-_])|smallcaps/i.test(font);
const fontFamily = (font: string) => font.split(/[-,]/)[0];

const LIGATURES: { [ligature: string]: string } = { "ﬀ": "ff", "ﬁ": "fi", "ﬂ": "fl", "ﬃ": "ffi", "ﬄ": "ffl", "ﬅ": "st", "ﬆ": "st" };
const cleanText = (str: string) => str.replace(/[ﬀ-ﬆ]/g, c => LIGATURES[c] ?? c);

const escapeHtml = (str: string) => str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------

interface Piece {
  text: string;
  font: string;
  size: number;
  x: number;
  end: number;
  space_before: boolean;
  gap_before: number;
}

export interface Line {
  page: number;
  column: "left" | "right" | "full";
  y: number;
  x: number;
  end: number;
  size: number;
  key: string;   // The style most of the line is in.
  font: string;
  text: string;
  pieces: Piece[];
  near_edge: boolean; // Near the top or bottom of the page, where running headers and footers go.
}

const lineText = (pieces: Piece[]) => pieces.map(p => `${p.space_before ? " " : ""}${p.text}`).join("").trim();

// Turns the pdf.js items of one line (left to right) into pieces of text with spacing worked out.
// Spacing comes from where things are, because the space items pdf.js gives aren't reliable
// (small caps fonts put zero-width spaces between letters of the same word).
const buildPieces = (items: PdfTextItem[]): Piece[] => {
  const max_small_caps_size: { [font: string]: number } = {};
  for (const item of items) {
    if (isSmallCapsFont(item.font)) {
      max_small_caps_size[item.font] = Math.max(max_small_caps_size[item.font] ?? 0, item.size);
    }
  }
  const pieces: Piece[] = [];
  let pending_space: PdfTextItem | null = null;
  for (const item of items) {
    if (item.str.trim() === "") {
      pending_space = item;
      continue;
    }
    let text = cleanText(item.str);
    // In small caps the bigger pieces are capitals and the smaller ones lowercase,
    // whatever case pdf.js gives them back in.
    const small_caps = isSmallCapsFont(item.font);
    const lowercase_small_caps = small_caps && item.size < max_small_caps_size[item.font] - 0.5;
    if (small_caps) {
      text = lowercase_small_caps ? text.toLowerCase() : text.toUpperCase();
    }
    const previous = pieces[pieces.length - 1];
    const gap = previous ? item.x - previous.end : 0;
    let space_before = false;
    if (previous) {
      if (lowercase_small_caps && previous.font === item.font) {
        // A word never starts with a lowercase small capital, so this continues the word before it.
        space_before = gap > item.size * 0.5;
      } else if (pending_space && item.x >= pending_space.x + pending_space.width * 0.5) {
        space_before = true;
      } else if (gap > item.size * 0.22) {
        space_before = true;
      }
      if (previous.text.endsWith(" ") || text.startsWith(" ")) {
        space_before = false;
      }
    }
    pending_space = null;
    pieces.push({ text, font: item.font, size: item.size, x: item.x, end: item.x + item.width, space_before, gap_before: gap });
  }
  return pieces;
};

const makeLine = (page: PdfPageText, column: Line["column"], items: PdfTextItem[]): Line | null => {
  const sorted = [...items].sort((a, b) => a.x - b.x);
  const pieces = buildPieces(sorted);
  if (pieces.length === 0) {
    return null;
  }
  // The style most of the line is in. Small caps lines are counted at their capitals' size.
  const chars: { [font: string]: number } = {};
  for (const piece of pieces) {
    chars[piece.font] = (chars[piece.font] ?? 0) + piece.text.length;
  }
  const font = Object.keys(chars).sort((a, b) => chars[b] - chars[a])[0];
  const sizes: { [size: number]: number } = {};
  for (const piece of pieces.filter(p => p.font === font)) {
    sizes[piece.size] = (sizes[piece.size] ?? 0) + piece.text.length;
  }
  const size = isSmallCapsFont(font)
    ? Math.max(...pieces.filter(p => p.font === font).map(p => p.size))
    : +Object.keys(sizes).sort((a, b) => sizes[+b] - sizes[+a])[0];
  const y = Math.max(...items.map(o => o.y));
  return {
    page: page.number,
    column,
    y,
    x: pieces[0].x,
    end: pieces[pieces.length - 1].end,
    size,
    key: `${font}@${size}`,
    font,
    text: lineText(pieces),
    pieces,
    near_edge: y < page.height * 0.12 || y > page.height * 0.9,
  };
};

// The usual left edge of the right column, across the whole document.
const findRightColumnX = (pages: PdfPageText[]): number => {
  const counts: { [x: number]: number } = {};
  for (const page of pages) {
    const mid = page.width / 2;
    for (const item of page.items) {
      if (item.str.trim() !== "" && item.x >= mid && item.x < mid + page.width * 0.15) {
        const x = Math.round(item.x);
        counts[x] = (counts[x] ?? 0) + 1;
      }
    }
  }
  const best = Object.keys(counts).sort((a, b) => counts[+b] - counts[+a])[0];
  return best !== undefined ? +best : 0;
};

// Puts one page's lines into reading order: the left column then the right column, except where
// something spans both columns (a wide table), which is read across at its place on the page.
const pageLines = (page: PdfPageText, right_column_x: number): Line[] => {
  const mid = page.width / 2;
  // Page furniture: running headers and footers, page numbers.
  const items = page.items.filter(o => o.y > 40 && o.y < page.height - 30);

  // Rows: items that sit on the same baseline.
  const rows: { y: number, items: PdfTextItem[] }[] = [];
  for (const item of [...items].sort((a, b) => b.y - a.y)) {
    const row = rows.find(o => Math.abs(o.y - item.y) < 1.5);
    if (row) {
      row.items.push(item);
    } else {
      rows.push({ y: item.y, items: [item] });
    }
  }

  const isFullWidth = (row: { items: PdfTextItem[] }) => {
    const text_items = row.items.filter(o => o.str.trim() !== "");
    if (text_items.some(o => o.x < mid - 8 && o.x + o.width > mid + 8)) {
      return true;
    }
    const left = text_items.filter(o => o.x < mid);
    const right = text_items.filter(o => o.x >= mid);
    if (left.length === 0 || right.length === 0) {
      return false;
    }
    // When what's on the right doesn't start where the right column starts, and it's in the same
    // font family as what's on the left at the same height, it's one row going across the page.
    // (Only where the right side starts matters: lines in the right column can end in a separate hyphen.)
    const right_start = right.reduce((a, b) => (b.x < a.x ? b : a), right[0]);
    return Math.abs(right_start.x - right_column_x) > 15 &&
      left.some(o => fontFamily(o.font) === fontFamily(right_start.font));
  };

  // Bands of full width rows (close together), top to bottom.
  const full_rows = rows.filter(isFullWidth);
  const bands: { top: number, bottom: number }[] = [];
  for (const row of full_rows) {
    const band = bands[bands.length - 1];
    if (band && band.bottom - row.y < 30) {
      band.bottom = row.y;
    } else {
      bands.push({ top: row.y, bottom: row.y });
    }
  }
  const inBand = (y: number) => bands.find(b => y <= b.top + 1 && y >= b.bottom - 1);

  const lines: Line[] = [];
  const addColumns = (region_rows: typeof rows) => {
    for (const side of ["left", "right"] as const) {
      for (const row of region_rows) {
        const side_items = row.items.filter(o => side === "left" ? o.x < mid : o.x >= mid);
        const line = side_items.length > 0 ? makeLine(page, side, side_items) : null;
        if (line) {
          lines.push(line);
        }
      }
    }
  };
  let region: typeof rows = [];
  let current_band: { top: number, bottom: number } | undefined;
  for (const row of rows) {
    const band = inBand(row.y);
    if (band) {
      if (band !== current_band) {
        addColumns(region);
        region = [];
        current_band = band;
      }
      const line = makeLine(page, "full", row.items);
      if (line) {
        lines.push(line);
      }
    } else {
      current_band = undefined;
      region.push(row);
    }
  }
  addColumns(region);
  return lines;
};

// Running headers and footers that slipped past the position check: lines near the top or
// bottom of the page that show up on a lot of pages. Only those near the edges count, since
// lines like a stat block's "AC 17" repeat on lots of pages too.
const dropRepeatedLines = (lines: Line[], page_count: number): Line[] => {
  const normalize = (text: string) => text.toLowerCase().replace(/\d+/g, "#");
  const pages_with: { [text: string]: Set<number> } = {};
  for (const line of lines.filter(l => l.near_edge)) {
    const text = normalize(line.text);
    (pages_with[text] ??= new Set()).add(line.page);
  }
  const threshold = Math.max(8, page_count * 0.25);
  return lines.filter(line => !(line.near_edge && (
    /^\d+$/.test(line.text) ||
    (line.text.length < 80 && pages_with[normalize(line.text)].size > threshold)
  )));
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

export function analyzePdf(pages: PdfPageText[]): PdfAnalysis {
  const right_column_x = findRightColumnX(pages);
  const lines = dropRepeatedLines(pages.flatMap(page => pageLines(page, right_column_x)), pages.length);

  const styles: { [key: string]: TextStyle & { next_families: { [family: string]: number } } } = {};
  lines.forEach((line, i) => {
    const style = styles[line.key] ??= {
      key: line.key, font: line.font, size: line.size, chars: 0, lines: 0, samples: [], level: 0, suggested_level: 0, next_families: {},
    };
    style.chars += line.text.length;
    style.lines++;
    if (style.samples.length < 5 && line.text.length > 1 && !style.samples.includes(line.text)) {
      style.samples.push(line.text.slice(0, 60));
    }
    const next = lines[i + 1];
    if (next) {
      const family = fontFamily(next.font);
      style.next_families[family] = (style.next_families[family] ?? 0) + 1;
    }
  });
  const all = Object.values(styles);
  const body = all.reduce((a, b) => (b.chars > a.chars ? b : a), all[0]);
  if (!body) {
    return { lines, styles: [], body_key: "" };
  }
  const body_family = fontFamily(body.font);

  // Headings: bold, noticeably bigger than the body text, and short.
  const candidates = all.filter(s =>
    isBoldFont(s.font) && s.size >= body.size + 1.5 && s.chars / s.lines < 60
  );
  const heading_families = new Set(candidates.map(s => fontFamily(s.font)));
  // A style that's usually followed by some other kind of text entirely (like a stat block's
  // name, followed by the stat block's font) titles a block of text rather than starting a section.
  const headings = candidates.filter(s => {
    const total = Object.values(s.next_families).reduce((a, b) => a + b, 0);
    const other = Object.entries(s.next_families)
      .filter(([family]) => family !== body_family && !heading_families.has(family))
      .reduce((a, [, count]) => a + count, 0);
    return total === 0 || other / total <= 0.5;
  });
  const sizes = Array.from(new Set(headings.map(s => s.size))).sort((a, b) => b - a);
  for (const style of headings) {
    style.suggested_level = sizes.indexOf(style.size) + 1;
    style.level = style.suggested_level;
  }

  const result: TextStyle[] = all
    .map(s => ({ key: s.key, font: s.font, size: s.size, chars: s.chars, lines: s.lines, samples: s.samples, level: s.level, suggested_level: s.suggested_level }))
    .sort((a, b) => b.size - a.size || b.chars - a.chars);
  return { lines, styles: result, body_key: body.key };
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

interface Segment {
  text: string;
  bold: boolean;
  italic: boolean;
}

const SENTENCE_END = /[.!?:;"”’)]$/;

const segmentsFor = (pieces: Piece[]): Segment[] => pieces.map(p => ({
  text: `${p.space_before ? " " : ""}${p.text}`,
  bold: isBoldFont(p.font),
  italic: isItalicFont(p.font),
}));

// Joins the lines of a paragraph, putting words hyphenated across lines back together.
const joinLines = (lines: Line[]): Segment[] => {
  const segments: Segment[] = [];
  let previous_text = "";
  for (const line of lines) {
    const line_segments = segmentsFor(line.pieces);
    if (segments.length > 0 && line_segments.length > 0) {
      const last = segments[segments.length - 1];
      const first_text = line_segments[0].text.trimStart();
      // The hyphen can be its own piece, so check the line's text rather than the last piece.
      if (/[A-Za-z]-$/.test(previous_text) && /^[a-z]/.test(first_text)) {
        last.text = last.text.replace(/\s*-$/, "");
        line_segments[0] = { ...line_segments[0], text: first_text };
      } else if (!last.text.endsWith(" ")) {
        line_segments[0] = { ...line_segments[0], text: ` ${first_text}` };
      }
    }
    segments.push(...line_segments);
    previous_text = line.text;
  }
  return segments;
};

const renderSegments = (segments: Segment[]): string => {
  // Merge neighbors with the same formatting, then wrap them.
  const merged: Segment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (last && last.bold === segment.bold && last.italic === segment.italic) {
      last.text += segment.text;
    } else {
      merged.push({ ...segment });
    }
  }
  return merged.map(s => {
    const leading = s.text.match(/^\s*/)?.[0] ?? "";
    const trailing = s.text.match(/\s*$/)?.[0] ?? "";
    let html = escapeHtml(s.text.trim());
    if (html === "") {
      return s.text;
    }
    if (s.italic) html = `<i>${html}</i>`;
    if (s.bold) html = `<b>${html}</b>`;
    return `${leading}${html}${trailing}`;
  }).join("").trim();
};

// Pieces split where there's a wide gap between them, which is what separates table cells.
const lineCells = (line: Line): { x: number, pieces: Piece[] }[] => {
  const cells: { x: number, pieces: Piece[] }[] = [];
  for (const piece of line.pieces) {
    if (cells.length === 0 || piece.gap_before > Math.max(piece.size * 0.8, 6)) {
      cells.push({ x: piece.x, pieces: [{ ...piece, space_before: false }] });
    } else {
      cells[cells.length - 1].pieces.push(piece);
    }
  }
  return cells;
};

const renderTable = (lines: Line[]): string => {
  const line_cells = lines.map(lineCells);
  // Columns line up with the cells of the row that has the most of them.
  const widest = line_cells.reduce((a, b) => (b.length > a.length ? b : a), line_cells[0]);
  const anchors = widest.map(c => c.x);
  const columnOf = (x: number) => {
    let column = 0;
    anchors.forEach((anchor, i) => {
      if (anchor <= x + 8) column = i;
    });
    return column;
  };
  type Row = { cells: Segment[][], full_width: boolean, header: boolean };
  const rows: Row[] = [];
  line_cells.forEach((cells, line_index) => {
    const placed: Segment[][] = anchors.map(() => []);
    for (const cell of cells) {
      placed[columnOf(cell.x)].push(...segmentsFor(cell.pieces));
    }
    const all_italic = cells.every(c => c.pieces.every(p => isItalicFont(p.font)));
    const previous = rows[rows.length - 1];
    if (cells.length === 1 && columnOf(cells[0].x) === 0 && all_italic && anchors.length > 1) {
      // A category row, like "Simple Melee Weapons".
      rows.push({ cells: [placed[0]], full_width: true, header: false });
    } else if (previous && !previous.full_width && placed[0].length === 0) {
      // Text wrapped onto another line within the cells of the row above.
      placed.forEach((segments, i) => {
        const cell = previous.cells[i];
        if (segments.length > 0 && cell) {
          const last = cell[cell.length - 1];
          const first_text = segments[0].text.trimStart();
          if (last && /[A-Za-z]-$/.test(last.text) && /^[a-z]/.test(first_text)) {
            // A word hyphenated across the lines.
            last.text = last.text.slice(0, -1);
            cell.push({ ...segments[0], text: first_text }, ...segments.slice(1));
          } else {
            cell.push({ ...segments[0], text: ` ${first_text}` }, ...segments.slice(1));
          }
        }
      });
    } else {
      const header = line_index === 0 && cells.every(c => c.pieces.every(p => isBoldFont(p.font)));
      rows.push({ cells: placed, full_width: false, header });
    }
  });
  let html = `<table class="standard_table">`;
  for (const row of rows) {
    if (row.full_width) {
      html += `<tr><td colspan="${anchors.length}">${renderSegments(row.cells[0])}</td></tr>`;
    } else {
      const tag = row.header ? "th" : "td";
      html += `<tr>${row.cells.map(c => `<${tag}>${renderSegments(c)}</${tag}>`).join("")}</tr>`;
    }
  }
  return html + "</table>";
};

interface Block {
  html: string;
  compact: boolean;  // A one-line label/value kind of line, like "Range: 90 feet", that sits right under the one before.
  caption: boolean;  // A short bold line that could be a table's title.
}

export function buildSections(analysis: PdfAnalysis, levels: { [style_key: string]: number }): ImportedSection[] {
  const body = analysis.styles.find(s => s.key === analysis.body_key);
  const body_family = body ? fontFamily(body.font) : "";
  const body_size = body ? body.size : 10;

  const roots: ImportedSection[] = [];
  const stack: { level: number, section: ImportedSection, blocks: Block[] }[] = [];
  const front: { section: ImportedSection, blocks: Block[] } = { section: { name: "Introduction", content: "", page: 1, children: [] }, blocks: [] };
  const finished: { section: ImportedSection, blocks: Block[] }[] = [front];
  const currentBlocks = () => (stack.length > 0 ? stack[stack.length - 1].blocks : front.blocks);

  let paragraph: Line[] = [];
  let table: Line[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const html = renderSegments(joinLines(paragraph));
    const first = paragraph[0];
    const all_bold = paragraph.every(l => l.pieces.every(p => isBoldFont(p.font)));
    if (html !== "") {
      currentBlocks().push({
        html,
        compact: paragraph.length === 1 && fontFamily(first.font) !== body_family,
        caption: paragraph.length === 1 && all_bold && first.text.length < 60,
      });
    }
    paragraph = [];
  };
  const flushTable = () => {
    if (table.length === 0) return;
    let blocks = currentBlocks();
    let html = renderTable(table);
    // A short bold line right before a table is its title.
    const previous = blocks[blocks.length - 1];
    if (previous && previous.caption) {
      blocks.pop();
      html = `${previous.html}\n${html}`;
      // Tables float to wherever they fit on the page, which can be inside a later section.
      // When the title is the name of a section it's under, like the Weapons table, it goes there.
      const title = previous.html.replace(/<[^>]+>/g, "").trim().toLowerCase();
      const owner = stack.slice(0, -1).reverse().find(o => o.section.name.toLowerCase() === title);
      if (owner) {
        blocks = owner.blocks;
      }
    }
    blocks.push({ html, compact: false, caption: false });
    table = [];
  };
  const flush = () => {
    flushParagraph();
    flushTable();
  };

  const levelOf = (line: Line) => levels[line.key] ?? 0;
  const isTableLine = (line: Line) => {
    const cells = lineCells(line).length;
    return cells >= 3 || (cells >= 2 && fontFamily(line.font) !== body_family);
  };
  const samePlace = (a: Line, b: Line) => a.page === b.page && a.column === b.column;

  let previous: Line | null = null;
  for (const line of analysis.lines) {
    const level = levelOf(line);
    if (level > 0) {
      flush();
      const top = stack[stack.length - 1];
      // A heading that wraps onto a second line.
      if (previous && top && levelOf(previous) === level && previous.key === line.key &&
        samePlace(previous, line) && previous.y - line.y < line.size * 1.6) {
        top.section.name = `${top.section.name} ${line.text}`;
      } else {
        while (stack.length > 0 && stack[stack.length - 1].level >= level) {
          stack.pop();
        }
        const section: ImportedSection = { name: line.text, content: "", page: line.page, children: [] };
        const parent = stack[stack.length - 1];
        if (parent) {
          parent.section.children.push(section);
        } else {
          roots.push(section);
        }
        const entry = { level, section, blocks: [] as Block[] };
        stack.push(entry);
        finished.push(entry);
      }
      previous = line;
      continue;
    }

    // Tables: lines with wide gaps between pieces, and the lines that continue them.
    if (table.length > 0) {
      const last = table[table.length - 1];
      const continues = fontFamily(line.font) === fontFamily(last.font) &&
        Math.abs(line.size - last.size) < 0.8 &&
        (!samePlace(line, last) || last.y - line.y < line.size * 2.4);
      if (continues) {
        table.push(line);
        previous = line;
        continue;
      }
      flushTable();
    }
    if (isTableLine(line)) {
      flushParagraph();
      table.push(line);
      previous = line;
      continue;
    }

    // Paragraphs.
    const last = paragraph[paragraph.length - 1];
    let starts_new = !last;
    if (last) {
      const family_changed = fontFamily(line.font) !== fontFamily(last.font) || Math.abs(line.size - last.size) > 0.8;
      const starts_bold = isBoldFont(line.pieces[0].font) && !isBoldFont(last.pieces[last.pieces.length - 1].font);
      const ended = SENTENCE_END.test(last.text);
      if (family_changed || starts_bold || /^[•▪◦]/.test(line.text)) {
        starts_new = true;
      } else if (samePlace(line, last)) {
        const gap = last.y - line.y;
        starts_new = gap > line.size * 1.6 || gap < 0 || (line.x > last.x + 3 && ended);
      } else {
        // Carried over to the next column or page: it continues unless the last one finished.
        starts_new = ended;
      }
      // Bold, bigger than the body text, and not a heading: a title for what comes after it.
      if (isBoldFont(line.font) && line.size > body_size + 1) {
        starts_new = true;
      }
    }
    if (starts_new) {
      flushParagraph();
    }
    paragraph.push(line);
    previous = line;
  }
  flush();

  for (const { section, blocks } of finished) {
    section.content = blocks.map((block, i) => {
      if (i === 0) return block.html;
      return `${block.compact && blocks[i - 1].compact ? "\n" : "\n\n"}${block.html}`;
    }).join("");
  }
  return front.blocks.length > 0 ? [front.section, ...roots] : roots;
}

// How many sections there are, counting every level.
export const countSections = (sections: ImportedSection[]): number =>
  sections.reduce((total, s) => total + 1 + countSections(s.children), 0);
